import React, { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { Star, Trash2, Loader2 } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import type { User } from '@supabase/supabase-js';
import {
  fetchToolReviews,
  fetchMyReview,
  upsertMyReview,
  deleteMyReview,
  type ToolReview,
} from '../../utils/reviews';

interface ReviewsSectionProps {
  toolId: string;
  toolName: string;
}

function StarRow({
  value,
  onChange,
  size = 'md',
}: {
  value: number;
  onChange?: (n: number) => void;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [hover, setHover] = useState(0);
  const px = size === 'lg' ? 'w-6 h-6' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="inline-flex items-center gap-0.5" role="group" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        const interactive = !!onChange;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onClick={() => onChange?.(n)}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(0)}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'} p-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded`}
          >
            <Star
              className={`${px} ${filled ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
            />
          </button>
        );
      })}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

export function ReviewsSection({ toolId, toolName }: ReviewsSectionProps) {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [reviews, setReviews] = useState<ToolReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [myReview, setMyReview] = useState<ToolReview | null>(null);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setAuthChecked(true);
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setAuthChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const list = await fetchToolReviews(toolId);
      if (cancelled) return;
      setReviews(list);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [toolId]);

  useEffect(() => {
    if (!user) {
      setMyReview(null);
      return;
    }
    fetchMyReview(toolId, user.id).then((mine) => {
      setMyReview(mine);
      if (mine) {
        setRating(mine.rating);
        setTitle(mine.title);
        setBody(mine.body);
      }
    });
  }, [toolId, user]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (rating < 1 || rating > 5) {
      setFormError('Please choose a rating between 1 and 5 stars.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const { error } = await upsertMyReview({
      toolId,
      userId: user.id,
      rating,
      title,
      body,
    });
    if (error) {
      setFormError(error);
      setSubmitting(false);
      return;
    }
    const [updated, mine] = await Promise.all([
      fetchToolReviews(toolId),
      fetchMyReview(toolId, user.id),
    ]);
    setReviews(updated);
    setMyReview(mine);
    setShowForm(false);
    setSubmitting(false);
  }

  async function handleDelete() {
    if (!user || !myReview) return;
    if (!window.confirm('Delete your review? This cannot be undone.')) return;
    setSubmitting(true);
    const { error } = await deleteMyReview(toolId, user.id);
    if (!error) {
      setMyReview(null);
      setRating(0);
      setTitle('');
      setBody('');
      const updated = await fetchToolReviews(toolId);
      setReviews(updated);
    }
    setSubmitting(false);
  }

  const publicReviews = reviews.filter((r) => !user || r.user_id !== user.id);
  const total = reviews.length;
  const avg =
    total > 0
      ? Math.round(
          (reviews.reduce((s, r) => s + r.rating, 0) / total) * 10
        ) / 10
      : 0;

  return (
    <section className="mt-10 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Reviews of {toolName}
            </h2>
            {total > 0 ? (
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                <StarRow value={Math.round(avg)} size="sm" />
                <span className="font-medium text-gray-900">{avg.toFixed(1)}</span>
                <span>out of 5</span>
                <span className="text-gray-400">·</span>
                <span>
                  {total} review{total === 1 ? '' : 's'}
                </span>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                No reviews yet. Be the first.
              </p>
            )}
          </div>

          {authChecked && user && !showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {myReview ? 'Edit your review' : 'Write a review'}
            </button>
          )}

          {authChecked && !user && (
            <Link href="/login">
              <a className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Sign in to review
              </a>
            </Link>
          )}
        </div>

        {user && showForm && (
          <form
            onSubmit={handleSubmit}
            className="mb-8 p-5 rounded-xl border border-gray-200 bg-gray-50"
          >
            <h3 className="font-semibold text-gray-900 mb-3">
              {myReview ? 'Edit your review' : 'Share your experience'}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your rating
              </label>
              <StarRow value={rating} onChange={setRating} size="lg" />
            </div>

            <div className="mb-4">
              <label
                htmlFor="review-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Headline <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="review-title"
                type="text"
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`e.g. ${toolName} saved us hours every week`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="review-body"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Details <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                id="review-body"
                rows={4}
                maxLength={4000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="What did you like or dislike? Who is this tool best for?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{body.length} / 4000</p>
            </div>

            {formError && (
              <p className="text-sm text-red-600 mb-3" role="alert">
                {formError}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting || rating < 1}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {submitting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                )}
                {myReview ? 'Save changes' : 'Submit review'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              {myReview && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={submitting}
                  className="ml-auto inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                  Delete review
                </button>
              )}
            </div>
          </form>
        )}

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" aria-hidden="true" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No reviews yet.
          </div>
        ) : (
          <ul className="space-y-5">
            {myReview && (
              <li className="border border-blue-200 bg-blue-50/40 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <StarRow value={myReview.rating} size="sm" />
                    <span className="text-xs font-medium text-blue-700">
                      Your review
                    </span>
                  </div>
                  <time
                    dateTime={myReview.updated_at}
                    className="text-xs text-gray-500"
                  >
                    {formatDate(myReview.updated_at)}
                  </time>
                </div>
                {myReview.title && (
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {myReview.title}
                  </h4>
                )}
                {myReview.body && (
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {myReview.body}
                  </p>
                )}
              </li>
            )}
            {publicReviews.map((r) => (
              <li
                key={r.id}
                className="border border-gray-200 rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-1">
                  <StarRow value={r.rating} size="sm" />
                  <time
                    dateTime={r.created_at}
                    className="text-xs text-gray-500"
                  >
                    {formatDate(r.created_at)}
                  </time>
                </div>
                {r.title && (
                  <h4 className="font-semibold text-gray-900 mb-1">{r.title}</h4>
                )}
                {r.body && (
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {r.body}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
